import { useRef, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { LogEntry } from './LogViewer'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { cn } from '@/lib/utils'

export interface VirtualLogListProps {
  /** Log entries */
  logs: LogEntry[]
  /** Whether timestamps are shown */
  showTimestamps: boolean
  /** Whether auto-scroll is enabled */
  autoScroll: boolean
  /** Whether lines wrap */
  wrapLines: boolean
  /** Whether more logs are loading */
  isLoading?: boolean
  /** Whether there are more logs to load */
  hasMore?: boolean
  /** Handler for loading more logs */
  onLoadMore?: () => void
  /** Additional CSS classes */
  className?: string
}

const levelColors: Record<string, string> = {
  debug: 'text-gray-400',
  info: 'text-blue-400',
  warn: 'text-yellow-400',
  error: 'text-red-400',
}

function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toISOString().substring(11, 23)
}

/**
 * Virtualized log list using @tanstack/react-virtual.
 * 
 * @example
 * ```tsx
 * <VirtualLogList
 *   logs={logs}
 *   showTimestamps={true}
 *   autoScroll={true}
 *   wrapLines={false}
 * />
 * ```
 */
export function VirtualLogList({
  logs,
  showTimestamps,
  autoScroll,
  wrapLines,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  className,
}: VirtualLogListProps) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: logs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 20,
    overscan: 50,
  })

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logs.length > 0) {
      virtualizer.scrollToIndex(logs.length - 1, { align: 'end' })
    }
  }, [logs.length, autoScroll, virtualizer])

  // Infinite scroll: load more when near top
  const handleScroll = () => {
    if (!parentRef.current || !hasMore || isLoading || !onLoadMore) return
    const { scrollTop } = parentRef.current
    if (scrollTop < 100) {
      onLoadMore()
    }
  }

  return (
    <div
      ref={parentRef}
      className={cn(
        'flex-1 overflow-auto bg-[#1e1e1e] text-[#d4d4d4] font-mono text-xs',
        className
      )}
      onScroll={handleScroll}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-2">
          <LoadingSpinner size="sm" />
        </div>
      )}
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const log = logs[virtualRow.index]
          return (
            <div
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className={cn(
                'flex items-start px-2 hover:bg-white/5',
                wrapLines ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'
              )}
            >
              {/* Line number */}
              <span className="text-gray-500 select-none pr-3 min-w-[4ch] text-right">
                {log.lineNumber}
              </span>

              {/* Timestamp */}
              {showTimestamps && log.timestamp && (
                <span className="text-gray-500 pr-2">
                  {formatTimestamp(log.timestamp)}
                </span>
              )}

              {/* Level badge */}
              {log.level && (
                <span
                  className={cn(
                    'uppercase pr-2 font-semibold min-w-[5ch]',
                    levelColors[log.level] || 'text-gray-400'
                  )}
                >
                  {log.level}
                </span>
              )}

              {/* Content */}
              <span
                className={cn(
                  'flex-1',
                  log.level === 'error' && 'text-red-400',
                  log.level === 'warn' && 'text-yellow-400'
                )}
              >
                {log.content}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
